import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../services/analytics';

/**
 * Route-to-title mapping for public (non-dashboard) pages.
 * Dashboard pages set their own title via DashboardLayout.
 */
const PAGE_TITLES: Record<string, string> = {
  '/': 'Home',
  '/login': 'Login',
  '/signup': 'Sign Up',
  '/verify-email': 'Verify Email',
  '/forgot-password': 'Forgot Password',
  '/reset-password': 'Reset Password',
  '/accept-invite': 'Accept Invite',
  '/privacy-policy': 'Privacy Policy',
  '/terms-of-service': 'Terms of Service',
  '/contact': 'Contact Us',
  '/about': 'About Us',
  '/onboarding': 'Onboarding',
};

/**
 * Resolves the page title from the current pathname.
 * First checks exact match, then prefix match for dynamic routes.
 */
function resolveTitle(pathname: string): string {
  // Exact match
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];

  // Dashboard pages set their own title — skip
  if (pathname.startsWith('/dashboard')) return '';

  // Dynamic public routes
  if (pathname.startsWith('/blog/')) return 'Blog';
  if (pathname.startsWith('/features/')) return 'Features';
  if (pathname.startsWith('/walkthrough/')) return 'Walkthrough';

  return 'Reviio';
}

/**
 * Sends a GA4 page_view event on every client-side route change.
 * Also sets document.title for public pages so the GA event
 * captures a meaningful page title.
 *
 * Mount once inside <BrowserRouter>.
 * The initial page_view is suppressed in gtag config
 * (send_page_view: false) so this is the single source of truth.
 */
export default function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    // Set title for public pages (dashboard handles its own)
    // The SEO component handles setting document.title correctly.
    // We only resolve it here for tracking purposes if needed, 
    // but trackPageView reads document.title internally.
    // Small delay so document.title has time to settle
    const timer = setTimeout(() => {
      trackPageView(location.pathname + location.search);
    }, 150);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  return null;
}
